import { useState } from "react";
import { useTheme } from "@mui/material";
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { extractApiErrorMessage } from "utils/util";
import DSAlert from "views/system-design/DSAlert";
import DSBox from "views/system-design/DSBox";
import DSButton from "views/system-design/DSButton";
import DSCard from "views/system-design/DSCard";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSCardContent from "views/system-design/DSCardContent";
import DSSkeleton from "views/system-design/DSSkeleton";
import DSTypography from "views/system-design/DSTypography";
import sectionCardSx from "views/system-design/sectionCardSx";
import {
  DSTable,
  DSTableBody,
  DSTableCell,
  DSTableHead,
  DSTableRow,
} from "views/system-design/Table";
import { PaymentsRange } from "views/Dashboard/components/PaymentsRangeFilter";
import OrderModal from "views/Orders/components/OrderModal";
import useOrderByName from "../hooks/useOrderByName";
import { useRetailerDetail } from "../hooks/useRetailers";
import { isNotFoundError } from "../utils/errors";
import {
  clockSegments,
  formatDays,
  formatPercent,
  pluralize,
} from "../utils/retailerChart";
import RetailerKpiCard from "./RetailerKpiCard";
import { SEGMENT_LABELS, useSegmentColors } from "./useSegmentColors";

const numericCellSx = { textAlign: "right" as const, whiteSpace: "nowrap" };

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });

type Props = {
  id: string;
  range: PaymentsRange;
  /** Drops the selection: offered when the API says this is not a retailer (any more). */
  onClear: () => void;
};

/** One retailer: the split of its clocks, the numbers that matter, and what it ordered. */
const RetailerDetail = ({ id, range, onClear }: Props) => {
  const { palette } = useTheme();
  const colors = useSegmentColors();
  const { data, isLoading, error, refetch } = useRetailerDetail(id, range);
  const notFound = isNotFoundError(error);

  // The order whose modal is open (the Orders page's own modal, on the full order).
  const [openOrderName, setOpenOrderName] = useState<string>();
  const { data: openOrder, isFetching: isOpeningOrder } =
    useOrderByName(openOrderName);

  if (isLoading) {
    return <DSSkeleton variant="rounded" height={260} sx={{ mt: 2 }} />;
  }

  if (notFound) {
    return (
      // The selection stays (a shared link keeps explaining itself) until the
      // viewer clears it.
      <DSAlert
        severity="info"
        sx={{ mt: 2 }}
        action={
          <DSButton size="small" color="inherit" onClick={onClear}>
            Clear
          </DSButton>
        }
      >
        This company is no longer marked as a retailer in Shopify.
      </DSAlert>
    );
  }

  if (error || !data) {
    return (
      <DSAlert
        severity="error"
        sx={{ mt: 2 }}
        action={
          <DSButton size="small" color="inherit" onClick={() => refetch()}>
            Retry
          </DSButton>
        }
      >
        {extractApiErrorMessage(error, "Could not load this retailer")}
      </DSAlert>
    );
  }

  const segments = clockSegments(data);
  const pieData = (["sold", "notSold", "withoutId"] as const)
    .map((key) => ({ key, name: SEGMENT_LABELS[key], value: segments[key] }))
    .filter((part) => part.value > 0);

  return (
    <DSBox sx={{ mt: 2, display: "grid", gap: 2 }}>
      <DSTypography variant="h5" sx={{ fontFamily: "Gilroy-Bold" }}>
        {data.name}
      </DSTypography>

      {data.orders === 0 ? (
        <DSTypography variant="body2" color="text.secondary">
          No orders in this period
        </DSTypography>
      ) : (
        <>
          <DSBox
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xxs: "1fr",
                md: "minmax(260px, 340px) 1fr",
              },
              alignItems: "stretch",
            }}
          >
            <DSCard sx={sectionCardSx}>
              <DSCardContent>
                <DSTypography variant="subtitle2" sx={{ mb: 1 }}>
                  Clocks sent: {data.clocksSent}
                </DSTypography>
                <ResponsiveContainer width="100%" height={230}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      innerRadius={55}
                      outerRadius={85}
                      stroke="none"
                    >
                      {pieData.map((part) => (
                        <Cell key={part.key} fill={colors[part.key]} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: palette.paper.elevation8,
                        border: "none",
                        borderRadius: 8,
                      }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </DSCardContent>
            </DSCard>

            <DSBox
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: {
                  xxs: "repeat(2, 1fr)",
                  md: "repeat(3, 1fr)",
                },
                alignContent: "start",
              }}
            >
              <RetailerKpiCard label="Clocks sent" value={data.clocksSent} />
              <RetailerKpiCard label="Sold" value={data.clocksSold} />
              <RetailerKpiCard
                label="On shelf"
                value={data.clocksOnShelf}
                hint={
                  data.clocksWithoutId > 0
                    ? `${data.clocksWithoutId} of them have no clock id yet`
                    : undefined
                }
              />
              <RetailerKpiCard
                label="Sell-through"
                value={formatPercent(data.sellThroughPct)}
              />
              <RetailerKpiCard
                label="Average time to sale"
                value={formatDays(data.avgDaysToSale)}
                hint="From the order date to the registration, for registered clocks"
              />
              <RetailerKpiCard label="Orders" value={data.orders} />
            </DSBox>
          </DSBox>

          <DSBox
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xxs: "1fr",
                md: "minmax(260px, 340px) 1fr",
              },
              alignItems: "start",
            }}
          >
            <DSCard sx={sectionCardSx}>
              <DSCardContent>
                <DSTypography variant="subtitle2" sx={{ mb: 1 }}>
                  By model
                </DSTypography>
                <DSTable size="small">
                  <DSTableHead>
                    <DSTableRow>
                      <DSTableCell>SKU</DSTableCell>
                      <DSTableCell sx={numericCellSx}>Sent</DSTableCell>
                      <DSTableCell sx={numericCellSx}>Sold</DSTableCell>
                    </DSTableRow>
                  </DSTableHead>
                  <DSTableBody>
                    {data.bySku.map((row) => (
                      <DSTableRow key={row.sku} hover>
                        <DSTableCell>{row.sku}</DSTableCell>
                        <DSTableCell sx={numericCellSx}>{row.sent}</DSTableCell>
                        <DSTableCell sx={numericCellSx}>{row.sold}</DSTableCell>
                      </DSTableRow>
                    ))}
                  </DSTableBody>
                </DSTable>
              </DSCardContent>
            </DSCard>

            <DSCard sx={sectionCardSx}>
              <DSCardContent sx={{ overflowX: "auto" }}>
                <DSTypography variant="subtitle2" sx={{ mb: 1 }}>
                  {pluralize(data.ordersList.length, "order")}
                </DSTypography>
                {openOrderName && !isOpeningOrder && openOrder === null && (
                  <DSAlert
                    severity="warning"
                    sx={{ mb: 1 }}
                    onClose={() => setOpenOrderName(undefined)}
                  >
                    Could not find order {openOrderName}
                  </DSAlert>
                )}
                <DSTable size="small">
                  <DSTableHead>
                    <DSTableRow>
                      <DSTableCell>Order</DSTableCell>
                      <DSTableCell>Order date</DSTableCell>
                      <DSTableCell sx={numericCellSx}>Clocks</DSTableCell>
                      <DSTableCell sx={numericCellSx}>Sold</DSTableCell>
                    </DSTableRow>
                  </DSTableHead>
                  <DSTableBody>
                    {data.ordersList.map((order) => (
                      <DSTableRow key={order.orderName} hover>
                        <DSTableCell>
                          <DSButton
                            variant="text"
                            color="inherit"
                            size="small"
                            onClick={() => setOpenOrderName(order.orderName)}
                            sx={{
                              p: 0,
                              minWidth: 0,
                              minHeight: 0,
                              font: "inherit",
                              textTransform: "none",
                              textDecoration: "underline",
                              textUnderlineOffset: "3px",
                              boxShadow: "none",
                              borderRadius: 0,
                              "&:hover": {
                                backgroundColor: "transparent",
                                boxShadow: "none",
                                textDecoration: "underline",
                              },
                            }}
                          >
                            {order.orderName}
                          </DSButton>
                          {isOpeningOrder &&
                            openOrderName === order.orderName && (
                              <DSCircularProgress
                                size={12}
                                sx={{ ml: 1, verticalAlign: "middle" }}
                              />
                            )}
                        </DSTableCell>
                        <DSTableCell sx={{ whiteSpace: "nowrap" }}>
                          {formatDate(order.orderDate)}
                        </DSTableCell>
                        <DSTableCell sx={numericCellSx}>
                          {order.clocks}
                        </DSTableCell>
                        <DSTableCell sx={numericCellSx}>
                          {order.sold}
                        </DSTableCell>
                      </DSTableRow>
                    ))}
                  </DSTableBody>
                </DSTable>
              </DSCardContent>
            </DSCard>
          </DSBox>
        </>
      )}

      <OrderModal
        show={!!openOrderName && !!openOrder}
        order={openOrder ?? null}
        onClose={() => setOpenOrderName(undefined)}
      />
    </DSBox>
  );
};

export default RetailerDetail;
